<template>
  <div class="account-detail-container">
    <div class="account-detail-header">
      <span class="back" @click="$router.push('/profile')">&#8592;</span>
      <span class="title">账户明细</span>
    </div>
    <div class="account-detail-select">
      <div class="account-detail-select-input">
        {{ selectedParkingName || '加载中...' }}
      </div>
    </div>
    <div class="account-detail-tabs">
      <div :class="['account-detail-tab', active==='all' && 'active']" @click="active='all'">全部</div>
      <div :class="['account-detail-tab', active==='income' && 'active']" @click="active='income'">收入</div>
      <div :class="['account-detail-tab', active==='expense' && 'active']" @click="active='expense'">支出</div>
    </div>
    <div class="account-detail-list" ref="listContainer">
      <div v-if="loading && list.length === 0" class="account-detail-loading">加载中...</div>
      <div v-else-if="display.length === 0" class="account-detail-empty">
        <div class="empty-tip-icon">📊</div>
        <div class="empty-tip-text">暂无明细</div>
      </div>
      <template v-else>
        <div class="account-detail-item" v-for="(item,i) in display" :key="i">
          <div class="account-detail-item-left">
            <div class="account-detail-item-title">
              {{ item.title }}
              <span v-if="item.tags && item.tags.length > 0" class="account-detail-item-tags">
                <span v-for="(tag, tagIndex) in item.tags" :key="tagIndex" :class="['account-detail-tag', tag === '自动充值' ? 'tag-recharge' : 'tag-clear']">{{ tag }}</span>
              </span>
              <span v-if="item.productName" class="coupon-name">{{ item.productName }}</span>
            </div>
            <div class="account-detail-item-desc">
              {{ item.desc }}
              <span v-if="item.maskedPlate" class="plate-number">{{ item.maskedPlate }}</span>
            </div>
          </div>
          <div :class="['account-detail-item-right', item.type==='income' ? 'income' : 'expense']">
            {{ item.type==='income' ? '+' : '-' }}{{ item.amount }}
          </div>
        </div>
        <div v-if="loadingMore" class="account-detail-loading-more">
          <div class="loading-spinner"></div>
          <span>加载中...</span>
        </div>
        <div v-if="noMoreData && display.length > 0 && !loadingMore" class="account-detail-no-more">没有更多数据了</div>
      </template>
    </div>
  </div>
</template>

<script>
import request from '@/utils/request'
import { getMerchantRechargeRecordList } from '@/api/recharge'
import { getMonthlyCardOrderList } from '@/api/monthlyCard'
import { getCouponIssuanceRecordPage } from '@/api/coupon'
import { getBusinessAdminList } from '@/api/business'

export default {
  name: 'AccountDetail',
  data () {
    return {
      active: 'all',
      list: [],
      loading: false,
      loadingMore: false,
      noMoreData: false,
      selectedParkingName: '',
      currentParkingId: null,
      currentBusinessId: null,
      // 分页状态
      rechargePage: 1,
      rechargeHasMore: true,
      orderPage: 1,
      orderHasMore: true,
      couponPage: 1,
      couponHasMore: true,
      adminIds: [], // 缓存管理员ID列表
      adminOrderPages: {}, // 每个管理员的当前页码 { adminId: pageNo }
      adminOrderHasMore: {}, // 每个管理员是否还有更多数据 { adminId: boolean }
      // 滚动节流
      scrollTimer: null
    }
  },
  computed: {
    display () { 
      if (this.active==='all') return this.list
      return this.list.filter(i=>i.type===this.active) 
    }
  },
  async mounted () {
    await this.fetchDefaultParking()
    if (this.currentBusinessId) {
      await this.loadAccountDetails()
    }
    // 添加滚动监听
    window.addEventListener('scroll', this.handleScroll, { passive: true })
  },
  beforeUnmount () {
    // 移除滚动监听
    window.removeEventListener('scroll', this.handleScroll)
    if (this.scrollTimer) {
      clearTimeout(this.scrollTimer)
    }
  },
  methods: {
    async fetchDefaultParking () {
      try {
        // 获取用户ID
        let userId = localStorage.getItem('userId')
        if (!userId) {
          const token = localStorage.getItem('token')
          if (token) {
            try {
              const parts = token.split('.')
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1]))
                userId = payload.userId || payload.uid || payload.sub || null
              }
            } catch (e) { /* ignore */ }
          }
        }
        if (!userId) {
          console.warn('未能从本地获取userId，无法查询停车场')
          this.selectedParkingName = '未找到用户信息'
          return
        }

        // 查询商户列表
        let merchantList = []
        try {
          const merchantRes = await request({
            url: '/market/business/list',
            method: 'post',
            data: { administor: userId },
            timeout: 30000
          })
          merchantList = merchantRes?.data || []
          this.currentBusinessId = (merchantList && merchantList[0] && merchantList[0].businessId) || null
        } catch (merchantError) {
          console.warn('查询商户列表失败:', merchantError)
          this.selectedParkingName = '加载失败'
          return
        }

        if (merchantList.length === 0) {
          this.selectedParkingName = '暂无商户'
          return
        }

        // 获取所有商户的停车场ID
        const allParkIds = []
        merchantList.forEach(merchant => {
          if (merchant.parkIds && Array.isArray(merchant.parkIds)) {
            allParkIds.push(...merchant.parkIds)
          }
        })

        if (allParkIds.length === 0) {
          this.selectedParkingName = '暂无停车场'
          return
        }

        // 查询停车场信息
        try {
          const uniqueParkIds = [...new Set(allParkIds)]
          const parkingRes = await request({
            url: '/parking/info/list/by-ids',
            method: 'post',
            data: uniqueParkIds,
            timeout: 30000
          })
          const parkingList = (parkingRes && parkingRes.data) || []

          if (parkingList.length > 0) {
            // 默认显示第一个停车场
            const firstParking = parkingList[0]
            this.selectedParkingName = firstParking.parkingName || '未知停车场'
            this.currentParkingId = firstParking.parkingId || firstParking.id || firstParking.parkingCode
          } else {
            this.selectedParkingName = '暂无停车场'
          }
        } catch (parkingError) {
          console.warn('查询停车场失败:', parkingError)
          this.selectedParkingName = '加载失败'
        }
      } catch (e) {
        console.error('获取默认停车场失败:', e)
        this.selectedParkingName = '加载失败'
      }
    },
    async loadAccountDetails (isLoadMore = false) {
      if (!this.currentBusinessId) {
        console.warn('商户ID不存在，无法加载账户明细')
        return
      }

      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        // 重置分页状态
        this.rechargePage = 1
        this.rechargeHasMore = true
        this.orderPage = 1
        this.orderHasMore = true
        this.couponPage = 1
        this.couponHasMore = true
        this.noMoreData = false
        this.list = []
        // 重置管理员订单分页状态
        this.adminOrderPages = {}
        this.adminOrderHasMore = {}
      }

      try {
        // 并行获取三种类型的数据
        const [rechargeData, orderData, couponData] = await Promise.all([
          this.fetchRechargeRecords(isLoadMore),
          this.fetchMonthlyCardOrders(isLoadMore),
          this.fetchCouponIssuanceRecords(isLoadMore)
        ])

        // 合并所有记录并按时间排序
        const newRecords = [
          ...rechargeData,
          ...orderData,
          ...couponData
        ]

        if (isLoadMore) {
          // 追加新数据
          this.list = [...this.list, ...newRecords]
        } else {
          // 首次加载
          this.list = newRecords
        }

        // 按时间降序排列（最新的在前）
        this.list.sort((a, b) => {
          const timeA = typeof a.sortTime === 'number' ? a.sortTime : this.getSortTimestamp(a.time || a.desc)
          const timeB = typeof b.sortTime === 'number' ? b.sortTime : this.getSortTimestamp(b.time || b.desc)
          return timeB - timeA
        })

        // 检查是否还有更多数据
        this.noMoreData = !this.rechargeHasMore && !this.orderHasMore && !this.couponHasMore
      } catch (error) {
        console.error('加载账户明细失败:', error)
        if (!isLoadMore) {
          this.list = []
        }
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },
    async fetchRechargeRecords (isLoadMore = false) {
      if (!this.rechargeHasMore) {
        return []
      }

      try {
        const pageNo = isLoadMore ? this.rechargePage : 1
        const params = {
          pageNo: pageNo,
          pageSize: 100,
          businessId: this.currentBusinessId
        }
        const response = await getMerchantRechargeRecordList(params)
        const result = response?.data || response
        const records = result?.records || result?.data || []
        
        // 检查是否还有更多数据
        const total = result?.total || result?.totalCount || 0
        const hasMore = records.length === 100 && (total === 0 || pageNo * 100 < total)
        this.rechargeHasMore = hasMore
        if (hasMore) {
          this.rechargePage = pageNo + 1
        }
        
        return records.map(record => {
          const rechargeTime = record.rechargeTime || record.createTime
          // 根据账户类型显示标题
          const accountType = record.accountType || ''
          const title = accountType ? `充值-${accountType}` : '充值'
          // 获取充值金额
          const rawAmount = record.rechargeAmount || record.actualPayment || 0
          const numAmount = Number(rawAmount)
          // 根据金额正负决定类型：正数或0为收入，负数为支出
          const type = numAmount >= 0 ? 'income' : 'expense'
          // 存储绝对值，模板会根据type自动添加+或-符号
          const amount = this.formatAmount(Math.abs(numAmount))
          
          // 添加标签
          const tags = []
          if (record.autoClear === true || record.autoClear === '是' || record.autoClear === 1) {
            tags.push('自动清零')
          }
          if (record.autoRecharge === true || record.autoRecharge === '是' || record.autoRecharge === 1) {
            tags.push('自动充值')
          }
          
          return {
            title: title,
            desc: this.formatDateTime(rechargeTime, true),
            type: type,
            amount: amount,
            time: rechargeTime,
            sortTime: this.getSortTimestamp(rechargeTime, true),
            tags: tags
          }
        })
      } catch (error) {
        console.error('获取充值记录失败:', error)
        return []
      }
    },
    async fetchMonthlyCardOrders (isLoadMore = false) {
      if (!this.orderHasMore) {
        return []
      }

      try {
        if (!this.currentBusinessId) {
          console.warn('商户ID不存在，无法获取月卡订单')
          return []
        }

        // 第一步：根据商户ID获取该商户的管理员ID列表（只在首次加载时获取）
        if (!isLoadMore || !this.adminIds || this.adminIds.length === 0) {
          let adminIds = []
          try {
            const adminResponse = await getBusinessAdminList(this.currentBusinessId)
            const adminData = adminResponse?.data?.records || adminResponse?.data || []
            adminIds = (Array.isArray(adminData) ? adminData : [])
              .map(admin => admin.id || admin.adminId || admin.userId)
              .filter(Boolean)
            
            if (adminIds.length === 0) {
              console.warn('该商户没有管理员，无法获取月卡订单')
              this.orderHasMore = false
              return []
            }
            this.adminIds = adminIds
            // 初始化每个管理员的分页状态
            adminIds.forEach(adminId => {
              this.adminOrderPages[adminId] = 1
              this.adminOrderHasMore[adminId] = true
            })
          } catch (error) {
            console.error('获取商户管理员列表失败:', error)
            this.orderHasMore = false
            return []
          }
        }

        // 第二步：为每个管理员分别查询各自的下一页订单
        // 只查询还有更多数据的管理员
        const activeAdminIds = this.adminIds.filter(adminId => {
          return this.adminOrderHasMore[adminId] !== false
        })

        if (activeAdminIds.length === 0) {
          this.orderHasMore = false
          return []
        }

        const allOrders = []
        
        // 并行查询所有还有数据的管理员的订单（每个管理员查询各自的下一页）
        const orderPromises = activeAdminIds.map(adminId => {
          const pageNo = this.adminOrderPages[adminId] || 1
          const params = {
            orderType: 'B',
            userId: adminId
          }
          return getMonthlyCardOrderList(params, pageNo, 100)
            .then(response => {
              const records = Array.isArray(response?.data) ? response.data : []
              return { adminId, records }
            })
            .catch(error => {
              console.warn(`获取管理员 ${adminId} 的月卡订单失败:`, error)
              return { adminId, records: [] }
            })
        })

        const orderResults = await Promise.all(orderPromises)
        
        // 处理每个管理员的查询结果，更新分页状态
        orderResults.forEach(({ adminId, records }) => {
          allOrders.push(...records)
          
          // 更新该管理员的分页状态
          if (records.length === 100) {
            // 返回了满页，说明可能还有更多数据
            const currentPage = this.adminOrderPages[adminId] || 1
            this.adminOrderPages[adminId] = currentPage + 1
            this.adminOrderHasMore[adminId] = true
          } else {
            // 返回的数据少于100条，说明该管理员没有更多数据了
            this.adminOrderHasMore[adminId] = false
          }
        })

        // 检查是否还有更多数据：只要有一个管理员还有更多数据，就继续
        const hasMore = Object.values(this.adminOrderHasMore).some(hasMore => hasMore === true)
        this.orderHasMore = hasMore

        // 去重（根据订单ID）
        const uniqueOrders = []
        const orderIdSet = new Set()
        // 如果是加载更多，需要排除已存在的订单ID
        if (isLoadMore) {
          this.list.forEach(item => {
            if (item.orderId) {
              orderIdSet.add(item.orderId)
            }
          })
        }
        allOrders.forEach(order => {
          const orderId = order.orderId || order.id
          if (orderId && !orderIdSet.has(orderId)) {
            orderIdSet.add(orderId)
            uniqueOrders.push(order)
          }
        })

        return uniqueOrders.map(record => {
          const timeValue = record.buyTime || record.createdTime || record.utcCreated
          // 提取月卡套餐名称
          const productName = record.productName || (record.productDTO && record.productDTO.productName) || ''
          // 提取车牌号（可能是数组，取第一个）
          const plateNumbers = record.plateNumbers || []
          const plateNumber = Array.isArray(plateNumbers) && plateNumbers.length > 0 ? plateNumbers[0] : ''
          const maskedPlate = plateNumber ? this.maskPlateNumber(plateNumber) : ''
          
          // 描述只显示时间
          const desc = timeValue ? this.formatDateTime(timeValue) : ''
          
          return {
            title: '月卡购买',
            desc: desc,
            productName: productName, // 月卡套餐名称，单独存储用于样式控制
            maskedPlate: maskedPlate, // 车牌号，单独存储用于样式控制
            type: 'expense',
            amount: this.formatAmount(record.paidAmount || record.totalAmount || 0),
            time: timeValue,
            sortTime: this.getSortTimestamp(timeValue),
            orderId: record.orderId || record.id // 保存订单ID用于去重
          }
        })
      } catch (error) {
        console.error('获取月卡订单失败:', error)
        return []
      }
    },
    async fetchCouponIssuanceRecords (isLoadMore = false) {
      if (!this.couponHasMore) {
        return []
      }

      try {
        const pageNo = isLoadMore ? this.couponPage : 1
        const params = {
          pageNo: pageNo,
          pageSize: 100,
          merchantIds: [this.currentBusinessId]
        }
        const response = await getCouponIssuanceRecordPage(params)
        const result = response?.data || response
        const records = result?.records || result?.data || []
        
        // 检查是否还有更多数据
        const total = result?.total || result?.totalCount || 0
        const hasMore = records.length === 100 && (total === 0 || pageNo * 100 < total)
        this.couponHasMore = hasMore
        if (hasMore) {
          this.couponPage = pageNo + 1
        }
        
        return records.map(record => {
          const timeValue = record.merchantIssuanceTime || record.issuanceTime
          const couponName = record.couponName || ''
          const plateNumber = record.plateNumber || ''
          const maskedPlate = plateNumber ? this.maskPlateNumber(plateNumber) : ''
          
          // 标题只显示"优惠券发放"，优惠券名称单独存储用于样式控制
          // 描述只显示时间，车牌号显示在时间后面
          const desc = timeValue ? this.formatDateTime(timeValue) : ''
          
          return {
            title: '优惠券发放',
            desc: desc,
            couponName: couponName, // 优惠券名称，单独存储用于样式控制
            maskedPlate: maskedPlate, // 车牌号，单独存储用于样式控制
            type: 'expense',
            amount: this.formatAmount(record.merchantBalanceDeduct || record.couponValue || 0),
            time: timeValue,
            sortTime: this.getSortTimestamp(timeValue)
          }
        })
      } catch (error) {
        console.error('获取优惠券发放记录失败:', error)
        return []
      }
    },
    maskPlateNumber (plateNumber) {
      if (!plateNumber || typeof plateNumber !== 'string') return ''
      const trimmed = plateNumber.trim()
      if (trimmed.length <= 2) return trimmed
      // 保留前两位和最后一位，中间用****替换
      // 例如：豫Q123456 -> 豫Q****6
      if (trimmed.length <= 3) {
        // 如果只有3位，则显示前2位+*
        return trimmed.substring(0, 2) + '*'
      }
      // 大于3位：前2位 + **** + 最后1位
      const prefix = trimmed.substring(0, 2)
      const suffix = trimmed.charAt(trimmed.length - 1)
      return prefix + '****' + suffix
    },
    formatAmount (amount) {
      if (amount === null || amount === undefined) return '0.00'
      // 后端返回的金额已经是元为单位，直接格式化即可
      const numAmount = Number(amount)
      return numAmount.toFixed(2)
    },
    getSortTimestamp (dateTime, assumeUtc = false) {
      if (!dateTime) return 0
      const date = this.parseToDate(dateTime, assumeUtc)
      return date && !isNaN(date.getTime()) ? date.getTime() : 0
    },
    formatDateTime (dateTime, assumeUtc = false) {
      if (!dateTime) return '-'

      const date = this.parseToDate(dateTime, assumeUtc)
      if (isNaN(date.getTime())) {
        return typeof dateTime === 'string' ? dateTime : '-'
      }

      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    parseToDate (value, forceUtc = false) {
      if (value instanceof Date) return new Date(value.getTime())
      if (typeof value === 'number') return new Date(value)
      if (typeof value === 'string') {
        if (forceUtc) {
          let normalized = value.trim()

          normalized = normalized.replace(' ', 'T')
          normalized = normalized.replace(/([+-]\d{2})(\d{2})$/, '$1:$2')

          const hasTimezoneInfo = /[zZ]$|[+-]\d{2}:\d{2}$/.test(normalized)
          if (!hasTimezoneInfo) {
            normalized += 'Z'
          }

          const parsed = new Date(normalized)
          if (!isNaN(parsed.getTime())) return parsed
        } else {
          const parsed = new Date(value)
          if (!isNaN(parsed.getTime())) return parsed
        }
      }
      const fallback = new Date(value)
      return fallback
    },
    handleScroll () {
      // 节流处理
      if (this.scrollTimer) {
        clearTimeout(this.scrollTimer)
      }
      this.scrollTimer = setTimeout(() => {
        this.checkScrollBottom()
      }, 200)
    },
    checkScrollBottom () {
      // 如果正在加载更多或没有更多数据，则不触发
      if (this.loadingMore || this.noMoreData) {
        return
      }

      // 检查是否滚动到底部（距离底部100px内）
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop
      const windowHeight = window.innerHeight || document.documentElement.clientHeight
      const documentHeight = document.documentElement.scrollHeight || document.body.scrollHeight
      const distanceToBottom = documentHeight - scrollTop - windowHeight

      // 如果接近底部，触发加载更多
      if (distanceToBottom < 100) {
        // 检查是否还有更多数据可加载
        if (this.rechargeHasMore || this.orderHasMore || this.couponHasMore) {
          this.loadAccountDetails(true)
        } else {
          this.noMoreData = true
        }
      }
    }
  }
}
</script>

<style scoped>
.account-detail-container {
  width: 100vw;
  max-width: 430px;
  min-height: 100vh;
  background: #f7f8fa;
  margin: 0 auto;
  box-sizing: border-box;
  padding-bottom: 80px;
}

.account-detail-header {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border-bottom: 1px solid #f2f2f2;
  position: relative;
  font-size: 20px;
  font-weight: bold;
}

.account-detail-header .back {
  position: absolute;
  left: 0;
  height: 56px;
  display: flex;
  align-items: center;
  font-size: 26px;
  padding-left: 16px;
  cursor: pointer;
}

.account-detail-select {
  margin: 18px 16px 0;
}

.account-detail-select-input {
  width: 100%;
  background: #fff;
  border: 1.5px solid #eee;
  border-radius: 12px;
  padding: 12px 16px;
  font-size: 16px;
  color: #222;
  outline: none;
  display: flex;
  align-items: center;
}

.account-detail-tabs {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  background: #fff;
  border-bottom: 1.5px solid #eaeaea;
  position: sticky;
  top: 0;
  z-index: 2;
  margin: 18px 0 0;
}

.account-detail-tab {
  flex: 1;
  text-align: center;
  font-size: 16px;
  color: #b0b0b0;
  padding: 10px 0 8px;
  cursor: pointer;
  font-weight: 500;
  position: relative;
}

.account-detail-tab.active {
  color: #3572e6;
  font-weight: 600;
}

.account-detail-list {
  margin: 18px 0 0;
}

.account-detail-item {
  background: #fff;
  border-radius: 12px;
  margin: 0 16px 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 8px #e0e0e033;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.account-detail-item-left {
  flex: 1;
  min-width: 0;
}

.account-detail-item-title {
  font-size: 17px;
  font-weight: 500;
  color: #222;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  position: relative;
  overflow: visible;
}

.account-detail-item-title .coupon-name {
  color: #3572e6;
  position: absolute;
  left: calc(50% + 80px);
  transform: translateX(-50%);
  min-width: 200px;
  max-width: 250px;
  word-wrap: break-word;
  word-break: normal;
}

.account-detail-item-title .account-detail-item-tags {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  flex-wrap: wrap;
}

.account-detail-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.4;
}

.account-detail-tag.tag-recharge {
  background: #f0f9f0;
  color: #09bb07;
  border: 1px solid #d4f4d4;
}

.account-detail-tag.tag-clear {
  background: #fff0f0;
  color: #ff4444;
  border: 1px solid #ffd4d4;
}

.account-detail-item-desc {
  font-size: 14px;
  color: #888;
  display: flex;
  align-items: center;
  position: relative;
}

.account-detail-item-desc .plate-number {
  color: #3572e6;
  position: absolute;
  left: calc(50% + 80px);
  transform: translateX(-50%);
}

.account-detail-item-right {
  font-size: 18px;
  font-weight: bold;
  margin-left: 12px;
}

.account-detail-item-right.income {
  color: #09bb07;
}

.account-detail-item-right.expense {
  color: #ff4444;
}

.account-detail-loading {
  text-align: center;
  padding: 40px 20px;
  color: #888;
  font-size: 15px;
}

.account-detail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;
  text-align: center;
}

.empty-tip-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.6;
}

.empty-tip-text {
  font-size: 16px;
  color: #666;
}

.account-detail-loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #888;
  font-size: 14px;
  gap: 8px;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e0e0e0;
  border-top-color: #3572e6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.account-detail-no-more {
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 14px;
}
</style>
